/* eslint-disable react/jsx-pascal-case */
import { createPlaybackRecovery, ReconnectResult } from "../../../../Component/ivs/playbackRecovery";
import { getIvsPlayerConfig } from "../../../../Component/ivs/playerConfig";
import {
  create,
  isPlayerSupported,
  LogLevel,
  PlayerEventType,
  PlayerState,
} from "amazon-ivs-player";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Live_End_Modal from "../Modal/Live_End_Modal";
import LiveHostStatus from "../../../../Component/UI/LiveHostStatus";
import useReviewHostStatus from "../../../Broadcast/useReviewHostStatus";
import LiveSoundButton from "../../../../Component/UI/LiveSoundButton";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import { live_info_type } from "../../../User/Live/Live";

export type LivePlayerHandle = { unmute: (volume: number) => void };

interface LiveComponentProps {
  volume: number;
  onMutedChange?: (muted: boolean) => void;
  manage?: boolean;
  set_All_Visible?: (visible: boolean) => void;
  all_visible?: boolean;
  live_info: live_info_type;
  onPlaybackStarted?: (latencySeconds: number | (() => number)) => void;
  onPlaybackPaused?: () => void;
  onProductMetadata?: (item: any, publishedAt?: number) => void;
}

const Live_Component = React.forwardRef<LivePlayerHandle, LiveComponentProps>(({
  volume,
  onMutedChange,
  manage,
  set_All_Visible,
  all_visible,
  live_info,
  onPlaybackStarted,
  onPlaybackPaused,
  onProductMetadata,
}, ref) => {
  const reviewHost = useReviewHostStatus();
  const localReview = process.env.NODE_ENV !== "production" && !!document.querySelector('meta[name="fanbird-local-review"]');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<any>(null);
  const mutedCallback = useRef(onMutedChange);
  mutedCallback.current = onMutedChange;
  const reportMuted = (muted: boolean) => {
    setIsMuted(muted);
    mutedCallback.current?.(muted);
  };
  React.useImperativeHandle(ref, () => ({
    unmute: (selectedVolume: number) => {
      const player = playerRef.current;
      if (!player) return;
      player.setVolume(selectedVolume / 100);
      player.setMuted(false);
      reportMuted(false);
      if (player.isPaused()) player.play();
    },
  }), []);
  const callbacksRef = useRef({ onPlaybackStarted, onPlaybackPaused, onProductMetadata });
  callbacksRef.current = { onPlaybackStarted, onPlaybackPaused, onProductMetadata };
  const [isLoading, set_IsLoading] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState(false);
  const [hostCameraOff, setHostCameraOff] = useState(false);
  const [hostInterrupted, setHostInterrupted] = useState(false);
  const [hostMicMuted, setHostMicMuted] = useState(false);

  const [live_end_modal, set_Live_End_Modal] = useState(false);
  const open_Live_End = () => set_Live_End_Modal(!live_end_modal);

  useEffect(() => {
    // The isolated review has no stream; never initialize an empty media player.
    if (localReview) return;
    if (!isPlayerSupported) {
      console.warn("현재 브라우저는 IVS 플레이어를 지원하지 않습니다.");
      return;
    }

    const player = create(getIvsPlayerConfig());

    player.setLogLevel(LogLevel.ERROR);
    // 저지연 모드 + 끊김(비행기모드/네트워크단절 등) 이후 라이브 끝단으로 강제 점프.
    // Let IVS manage live latency; reconnect only when playback stops making progress.
    // setInitialBufferDuration은 건드리지 않는다 — 이전 실험에서 역효과(지연시간 3.3초로 악화) 확인됨.
    player.setLiveLowLatencyEnabled(true);
    player.setRebufferToLive(true);
    playerRef.current = player;

    if (videoRef.current) {
      player.attachHTMLVideoElement(videoRef.current);
    }

    let disposed = false;
    let accessDenied = false;
    let streamInfoPending = false;
    const controller = new AbortController();
    const getStreamInfo = async (canLoad = () => !disposed): Promise<ReconnectResult> => {
      if (accessDenied) return "stop";
      if (disposed || streamInfoPending || videoRef.current?.ownerDocument.hidden || !canLoad()) return "retry";
      streamInfoPending = true;
      try {
        const res = await api.post(url.Communication_url.getStreamInfo, {
          playback_only: true,
          live_seq: live_info?.seq,
        }, { signal: controller.signal, timeout: 10000 });
        if (!canLoad() || !res.data?.playUrl) return "retry";
        player.load(res.data.playUrl);
        return "loaded";
      } catch (error: any) {
        if (canLoad() && error?.response?.status === 403) {
          accessDenied = true;
          set_IsLoading(false);
          return "stop";
        }
        return "retry";
      } finally {
        streamInfoPending = false;
      }
    };
    const recovery = createPlaybackRecovery(player, async isCurrent => {
      if (accessDenied) return "stop";
      const { data } = await api.get(url.Communication_url.get_live_info, {
        params: { seq: live_info?.seq }, signal: controller.signal, timeout: 10000,
      });
      if (!isCurrent()) return "retry";
      if (data?.live_status === "종료") {
        set_IsLoading(false);
        if (!manage) set_Live_End_Modal(true);
        return "stop";
      }
      set_IsLoading(true);
      callbacksRef.current.onPlaybackPaused?.();
      return getStreamInfo(isCurrent);
    }, () => !videoRef.current?.ownerDocument.hidden);
    void getStreamInfo();

    let mutedPlaybackRetried = false;
    const onPlaybackBlocked = () => {
      set_IsLoading(false);
      // Retry automatically with muted playback when the browser blocks audio.
      if (!mutedPlaybackRetried && !player.isMuted()) {
        mutedPlaybackRetried = true;
        player.setMuted(true);
        reportMuted(true);
        try { player.play(); } catch { recovery.failed(); }
      } else {
        recovery.failed();
      }
    };
    const onReady = () => {
      mutedPlaybackRetried = false;
      try { player.play(); } catch { recovery.failed(); }
    };
    const onMutedChanged = () => reportMuted(player.isMuted());
    const onPlaying = () => {
      recovery.playing();
      onMutedChanged();
      set_IsLoading(false);
      callbacksRef.current.onPlaybackStarted?.(() => player.getLiveLatency());
    };
    const onEnded = () => {
      callbacksRef.current.onPlaybackPaused?.();
      set_IsLoading(true);
      recovery.failed();
    };
    const onBuffering = () => {
      callbacksRef.current.onPlaybackPaused?.();
      set_IsLoading(true);
      recovery.buffering();
    };
    const onError = () => {
      callbacksRef.current.onPlaybackPaused?.();
      set_IsLoading(true);
      recovery.failed();
    };

    const onMetadataCue = (cue: { text?: string }) => {
      try {
        const metadata = JSON.parse(cue.text || '');
        const statusValue = (key: string) => typeof metadata[key] === 'boolean'
          ? metadata[key]
          : metadata.action === key && typeof metadata.value === 'boolean'
            ? metadata.value
            : undefined;
        const audioMuted = statusValue('audio_muted');
        if (audioMuted !== undefined) setHostMicMuted(audioMuted);
        const cameraOff = statusValue('camera_muted');
        if (cameraOff !== undefined) setHostCameraOff(cameraOff);
        const interrupted = statusValue('host_interrupted');
        if (interrupted !== undefined) setHostInterrupted(interrupted);
        if (metadata.action === 'stock_over' || metadata.action === 'stock') {
          callbacksRef.current.onProductMetadata?.(metadata.item, metadata.publishedAt);
        }
      } catch { /* Ignore unrelated metadata. */ }
    };

    const onPaused = () => {
      callbacksRef.current.onPlaybackPaused?.();
      const state = player.getState();
      if (!disposed && player.isPaused() && [PlayerState.READY, PlayerState.PLAYING].includes(state)) {
        try { player.play(); } catch { recovery.failed(); }
      }
    };
    player.addEventListener(PlayerState.IDLE, onPaused);
    player.addEventListener(PlayerState.READY, onReady);
    player.addEventListener(PlayerState.PLAYING, onPlaying);
    player.addEventListener(PlayerState.ENDED, onEnded);
    player.addEventListener(PlayerState.BUFFERING, onBuffering);
    player.addEventListener(PlayerEventType.ERROR, onError);
    player.addEventListener(PlayerEventType.MUTED_CHANGED, onMutedChanged);
    player.addEventListener(PlayerEventType.AUDIO_BLOCKED, onPlaybackBlocked);
    player.addEventListener(PlayerEventType.PLAYBACK_BLOCKED, onPlaybackBlocked);
    player.addEventListener(PlayerEventType.TEXT_METADATA_CUE, onMetadataCue);

    player.setVolume(volume / 100);

    return () => {
      disposed = true;
      recovery.dispose();
      controller.abort();
      player.removeEventListener(PlayerState.IDLE, onPaused);
      player.removeEventListener(PlayerState.READY, onReady);
      player.removeEventListener(PlayerState.PLAYING, onPlaying);
      player.removeEventListener(PlayerState.ENDED, onEnded);
      player.removeEventListener(PlayerState.BUFFERING, onBuffering);
      player.removeEventListener(PlayerEventType.ERROR, onError);
      player.removeEventListener(PlayerEventType.MUTED_CHANGED, onMutedChanged);
      player.removeEventListener(PlayerEventType.AUDIO_BLOCKED, onPlaybackBlocked);
      player.removeEventListener(PlayerEventType.PLAYBACK_BLOCKED, onPlaybackBlocked);
      player.removeEventListener(PlayerEventType.TEXT_METADATA_CUE, onMetadataCue);
      player.delete();
    };
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.setVolume(volume / 100);
      playerRef.current.setMuted(volume === 0);
    }
  }, [volume]);

  const Mute_Off = () => {
    playerRef.current.setMuted(false);
    playerRef.current.setVolume(volume / 100);
    reportMuted(false);
  };

  // console.log(playerRef.current && playerRef.current.getState());
  // console.log(playerRef.current && playerRef.current.getVolume());
  // console.log(playerRef.current && playerRef.current.isPaused());
  // console.log(playerRef.current && playerRef.current.isMuted());

  return (
    <VideoContainer translate="no">
      <LiveSoundButton
        visible={volume > 0 && isMuted && !isLoading && !!playerRef.current && !playerRef.current.isPaused()}
        onUnmute={Mute_Off}
      />
      <video
        crossOrigin="anonymous"
        onClick={() => {
          playerRef.current &&
            !playerRef.current.isPaused() &&
            !playerRef.current.isMuted() &&
            set_All_Visible &&
            set_All_Visible(!all_visible);
        }}
        ref={videoRef}
        autoPlay
        controls={false}
        playsInline
      />
      <LiveHostStatus interrupted={hostInterrupted} cameraOff={reviewHost?.cameraOff ?? hostCameraOff} micMuted={reviewHost?.micMuted ?? hostMicMuted} />
      <Live_End_Modal visible={live_end_modal} handleModal={open_Live_End} />
    </VideoContainer>
  );
});

export default Live_Component;

const VideoContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;

  video {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  video::-webkit-media-controls-timeline,
  video::-webkit-media-controls-current-time-display {
    display: none;
  }
`;

